import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useVocabulary } from './contexts/VocabularyContext';
import { VocabularyTabs } from './components/VocabularyTabs';
import { authFetch } from './common/authFetch';
import './css/Vocabulary.css';
import './css/VocabularyQuiz.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// 오늘 날짜를 KST 기준으로 구한다.
// 브라우저 로컬 시간대를 쓰면 해외/UTC 환경에서 하루 어긋난 날짜로 업로드된다.
// en-CA 로케일은 YYYY-MM-DD 형식을 준다.
const todayStr = () => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Seoul',
  year: 'numeric', month: '2-digit', day: '2-digit',
}).format(new Date());

const Vocabulary = () => {
  const { t } = useTranslation();
  const {
    dates, words, pagination, loading,
    fetchDates, fetchWords, importCsv, updateDateAlias, createWord, updateWord, deleteWords,
    studentId: selectedStudentId, setStudentId: setSelectedStudentId,
  } = useVocabulary();

  const [selectedDate, setSelectedDate] = useState('');
  // 날짜를 고르지 않고 전체 기간을 한 번에 보는 모드 (날짜별 구분은 그대로 유지)
  const [viewAll, setViewAll] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [expandedMonths, setExpandedMonths] = useState([]);
  const [pageSize, setPageSize] = useState(50);
  const [onlyImportant, setOnlyImportant] = useState(false);
  // 기본 정렬은 CSV 원본 순서
  const [sort, setSort] = useState('seq');
  const [order, setOrder] = useState('asc');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadDate, setUploadDate] = useState(todayStr());
  // 단어장 이름. 비우면 날짜가 그대로 이름이 된다
  const [uploadAlias, setUploadAlias] = useState('');
  // 날짜 이름을 고치는 중인 날짜와 입력값
  const [aliasEditing, setAliasEditing] = useState(null);
  const [aliasDraft, setAliasDraft] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  // 다중 삭제용 선택 상태
  const [selectedIds, setSelectedIds] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ word: '', meaning: '', frequency: '', is_important: false, note: '', extra: '' });

  // 관찰 대상 선택 (관찰자만 표시). 선택 자체는 컨텍스트에 있어 화면 간 유지된다
  const [myStudents, setMyStudents] = useState([]);
  const fileInputRef = useRef(null);
  // 관찰 대상이 바뀌었는지 판단해 날짜를 다시 고를지 결정한다
  const lastTargetRef = useRef(null);

  const targetUserId = selectedStudentId || undefined;

  useEffect(() => {
    const loadStudents = async () => {
      try {
        const res = await authFetch(`${API_URL}/api/v1/observer/students`);
        const data = await res.json();
        if (data.success) {
          setMyStudents((data.students || []).filter(s => s.perm_vocabulary));
        }
      } catch (e) {
        console.error('Failed to fetch students', e);
      }
    };
    loadStudents();
  }, []);

  const reloadDates = useCallback(async () => {
    const list = await fetchDates(targetUserId);
    if (list.length === 0) {
      setSelectedDate('');
      return;
    }

    // 화면에 처음 들어왔거나 관찰 대상을 바꿨을 때만 날짜를 새로 고른다
    const targetKey = targetUserId || '';
    const isNewTarget = lastTargetRef.current !== targetKey;
    lastTargetRef.current = targetKey;

    setSelectedDate(prev => {
      // 이미 보고 있던 날짜가 살아 있으면 유지한다.
      // 단어를 지우거나 고칠 때마다 오늘로 튕기면 작업을 이어갈 수 없다
      if (!isNewTarget && prev && list.some(d => d.study_date === prev)) return prev;

      // 기본 선택은 오늘(KST). 오늘 올린 단어가 없으면 가장 최근 날짜로 내려간다
      const today = todayStr();
      return list.some(d => d.study_date === today) ? today : list[0].study_date;
    });
  }, [fetchDates, targetUserId]);

  useEffect(() => {
    reloadDates();
  }, [reloadDates]);

  // 입력할 때마다 요청하지 않도록 지연시킨다
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // 현재 조회 조건으로 단어를 다시 불러온다 (조건이 여러 곳에서 쓰이므로 한 곳에 모음)
  const reloadWords = useCallback((page) => {
    if (!viewAll && !selectedDate) return;
    fetchWords({
      date: viewAll ? undefined : selectedDate,
      important: onlyImportant,
      q: debouncedSearch || undefined,
      page, limit: pageSize, sort, order, targetUserId,
    });
  }, [viewAll, selectedDate, onlyImportant, debouncedSearch, pageSize, sort, order, targetUserId, fetchWords]);

  useEffect(() => {
    reloadWords();
  }, [reloadWords]);

  // 목록이 바뀌면 선택을 비운다 — 화면에 없는 단어가 선택된 채 남아 있으면 안 된다
  useEffect(() => {
    setSelectedIds([]);
  }, [viewAll, selectedDate, onlyImportant, debouncedSearch, pageSize, sort, order, targetUserId, pagination.page]);

  // 날짜를 월별로 묶는다 (365개가 평면 나열되지 않도록)
  const monthGroups = useMemo(() => {
    const map = new Map();
    for (const d of dates) {
      const month = d.study_date.slice(0, 7);
      if (!map.has(month)) map.set(month, { month, days: [], words: 0 });
      const g = map.get(month);
      g.days.push(d);
      g.words += d.word_count;
    }
    return Array.from(map.values());
  }, [dates]);

  // 날짜를 고르면 그 달을 펼친다.
  // 펼침 여부를 상태 하나로만 판단해야 사용자가 직접 접을 수 있다.
  // (선택된 달을 무조건 펼침으로 계산하면 헤더를 눌러도 접히지 않는다)
  useEffect(() => {
    if (!selectedDate) return;
    const month = selectedDate.slice(0, 7);
    setExpandedMonths(prev => (prev.includes(month) ? prev : [...prev, month]));
  }, [selectedDate]);

  const isMonthOpen = (month) => expandedMonths.includes(month);

  const toggleMonth = (month) => {
    setExpandedMonths(prev =>
      prev.includes(month) ? prev.filter(m => m !== month) : [...prev, month]
    );
  };

  const toggleSelect = (wordId) => {
    setSelectedIds(prev =>
      prev.includes(wordId) ? prev.filter(id => id !== wordId) : [...prev, wordId]
    );
  };

  const allSelected = words.length > 0 && selectedIds.length === words.length;

  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : words.map(w => w.word_id));
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(t('vocabulary.deleteSelectedConfirm', { count: selectedIds.length }))) return;

    const deleted = await deleteWords(selectedIds, targetUserId);
    if (deleted !== null) {
      setSelectedIds([]);
      await reloadDates();
      reloadWords();
    }
  };

  // 같은 컬럼을 다시 누르면 방향만 뒤집는다
  const handleSort = (column) => {
    if (sort === column) {
      setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(column);
      setOrder('asc');
    }
  };

  const sortIndicator = (column) => {
    if (sort !== column) return <span className="vocab-sort-icon" aria-hidden="true">{'⇅'}</span>;
    return (
      <span className="vocab-sort-icon active" aria-hidden="true">
        {order === 'asc' ? '▲' : '▼'}
      </span>
    );
  };

  // 정렬 가능한 헤더 셀
  const SortableTh = ({ column, className, children }) => (
    <th
      className={`${className || ''} vocab-sortable${sort === column ? ' sorted' : ''}`}
      onClick={() => handleSort(column)}
      title={t('vocabulary.sortBy', { column: typeof children === 'string' ? children : '' })}
    >
      {children}{sortIndicator(column)}
    </th>
  );

  // 다이얼로그를 열 때마다 오늘(KST)로 되돌린다.
  // 직전 업로드에 쓴 날짜가 남아 있으면 다음 날 그대로 올려 엉뚱한 날짜에 쌓인다
  const openUploadDialog = () => {
    setUploadDate(todayStr());
    setUploadFile(null);
    setUploadResult(null);
    setUploadOpen(true);
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) {
      toast.error(t('vocabulary.csvOnly'));
      return;
    }
    setUploadFile(file);
    setUploadResult(null);
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error(t('vocabulary.selectFile'));
      return;
    }
    const result = await importCsv(uploadFile, uploadDate, targetUserId, uploadAlias.trim());
    if (result) {
      setUploadResult(result);
      setUploadFile(null);
      setUploadAlias('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      await reloadDates();
      setSelectedDate(uploadDate);
      reloadWords();
    }
  };

  const startAliasEdit = (e, d) => {
    // 날짜 선택으로 번지면 편집 중에 목록이 움직인다
    e.stopPropagation();
    setAliasEditing(d.study_date);
    setAliasDraft(d.alias && d.alias !== d.study_date ? d.alias : '');
  };

  const commitAliasEdit = async (studyDate) => {
    const saved = await updateDateAlias(studyDate, aliasDraft.trim(), targetUserId);
    if (saved === null) return;
    setAliasEditing(null);
    await reloadDates();
  };

  const startEdit = (w) => {
    setEditingId(w.word_id);
    setEditForm({
      word: w.word,
      meaning: w.meaning,
      frequency: w.frequency ?? '',
      is_important: !!w.is_important,
      note: w.note || '',
      extra: w.extra || '',
    });
  };

  const saveEdit = async (wordId) => {
    if (!editForm.word?.trim() || !editForm.meaning?.trim()) {
      toast.error(t('fillAllFields'));
      return;
    }
    const ok = await updateWord(wordId, editForm, targetUserId);
    if (ok) {
      setEditingId(null);
      reloadWords();
    }
  };

  const handleAdd = async () => {
    if (!addForm.word.trim() || !addForm.meaning.trim()) {
      toast.error(t('fillAllFields'));
      return;
    }
    const ok = await createWord({ ...addForm, study_date: selectedDate || todayStr() }, targetUserId);
    if (ok) {
      setAddForm({ word: '', meaning: '', frequency: '', is_important: false, note: '', extra: '' });
      setAddOpen(false);
      await reloadDates();
      reloadWords();
    }
  };

  return (
    <div className="vocab-container">
      <div className="vocab-header">
        <h2>{t('vocabulary.title')}</h2>
        <div className="vocab-header-actions">
          {myStudents.length > 0 && (
            <select
              className="vocab-select"
              value={selectedStudentId}
              onChange={(e) => setSelectedStudentId(e.target.value)}
            >
              <option value="">{t('vocabulary.myVocabulary')}</option>
              {myStudents.map(s => (
                <option key={s.user_id} value={s.user_id}>{s.username}</option>
              ))}
            </select>
          )}
          <button className="vocab-btn vocab-btn-primary" onClick={openUploadDialog}>
            {t('vocabulary.uploadCsv')}
          </button>
        </div>
      </div>

      <VocabularyTabs />

      <div className="vocab-body">
        {/* 날짜 목록 */}
        <aside className="vocab-dates">
          <h3>{t('vocabulary.byDate')}</h3>
          {dates.length === 0 && !loading && (
            <p className="vocab-empty">{t('vocabulary.noWords')}</p>
          )}

          {dates.length > 0 && (
            <ul className="vocab-viewall">
              <li
                className={viewAll ? 'active' : ''}
                onClick={() => { setViewAll(true); }}
              >
                <span className="vocab-date-label">{t('vocabulary.viewAll')}</span>
                <span className="vocab-date-count">
                  {dates.reduce((sum, d) => sum + d.word_count, 0)}
                </span>
              </li>
            </ul>
          )}

          {/* 날짜가 많아져도 훑을 수 있도록 월별로 접는다 */}
          {monthGroups.map(g => (
            <div className="vocab-month" key={g.month}>
              <button
                className={`vocab-month-head${isMonthOpen(g.month) ? ' open' : ''}`}
                onClick={() => toggleMonth(g.month)}
              >
                <span className="vocab-month-caret" aria-hidden="true">
                  {isMonthOpen(g.month) ? '▾' : '▸'}
                </span>
                <span className="vocab-month-label">{g.month}</span>
                <span className="vocab-month-count">
                  {t('vocabulary.monthSummary', { days: g.days.length, words: g.words })}
                </span>
              </button>

              {isMonthOpen(g.month) && (
                <ul>
                  {g.days.map(d => (
                    <li
                      key={d.study_date}
                      className={!viewAll && d.study_date === selectedDate ? 'active' : ''}
                      onClick={() => { setViewAll(false); setSelectedDate(d.study_date); }}
                    >
                      {aliasEditing === d.study_date ? (
                        <span className="vocab-alias-edit" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="text"
                            maxLength={100}
                            value={aliasDraft}
                            autoFocus
                            onChange={(e) => setAliasDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') commitAliasEdit(d.study_date);
                              if (e.key === 'Escape') setAliasEditing(null);
                            }}
                            placeholder={d.study_date}
                          />
                          <button
                            className="vocab-icon-btn"
                            onClick={() => commitAliasEdit(d.study_date)}
                            title={t('save')}
                          >
                            &#10003;
                          </button>
                          <button
                            className="vocab-icon-btn"
                            onClick={() => setAliasEditing(null)}
                            title={t('cancel')}
                          >
                            &#10005;
                          </button>
                        </span>
                      ) : (
                        <>
                          <span className="vocab-date-label" title={d.study_date}>
                            {d.alias && d.alias !== d.study_date ? d.alias : d.study_date.slice(5)}
                          </span>
                          <button
                            className="vocab-icon-btn vocab-alias-btn"
                            onClick={(e) => startAliasEdit(e, d)}
                            title={t('vocabulary.aliasEdit')}
                            aria-label={t('vocabulary.aliasEdit')}
                          >
                            &#9998;
                          </button>
                          <span className="vocab-date-count">
                            {d.word_count}
                            {d.important_count > 0 && <em> ★{d.important_count}</em>}
                          </span>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </aside>

        {/* 단어 테이블 */}
        <section className="vocab-words">
          <div className="vocab-toolbar">
            <div className="vocab-search">
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('vocabulary.searchPlaceholder')}
              />
              {search && (
                <button className="vocab-search-clear" onClick={() => setSearch('')} title={t('cancel')}>
                  &#10005;
                </button>
              )}
            </div>
            <label className="vocab-checkbox">
              <input
                type="checkbox"
                checked={onlyImportant}
                onChange={(e) => setOnlyImportant(e.target.checked)}
              />
              {t('vocabulary.onlyImportant')}
            </label>
            <span className="vocab-count">
              {t('vocabulary.totalWords', { count: pagination.total })}
              {selectedIds.length > 0 && (
                <em className="vocab-selected-count">
                  {' '}{t('vocabulary.selectedCount', { count: selectedIds.length })}
                </em>
              )}
            </span>
            <button
              className="vocab-btn vocab-btn-danger"
              onClick={handleDeleteSelected}
              disabled={selectedIds.length === 0}
            >
              {t('vocabulary.deleteSelected')}
            </button>
            {selectedDate && !viewAll && (
              <button className="vocab-btn" onClick={() => setAddOpen(!addOpen)}>
                {addOpen ? t('cancel') : t('vocabulary.addWord')}
              </button>
            )}
          </div>


          {addOpen && (
            <div className="vocab-add-row">
              <input
                placeholder={t('vocabulary.word')}
                value={addForm.word}
                onChange={(e) => setAddForm({ ...addForm, word: e.target.value })}
              />
              <input
                placeholder={t('vocabulary.meaning')}
                value={addForm.meaning}
                onChange={(e) => setAddForm({ ...addForm, meaning: e.target.value })}
              />
              <input
                type="number"
                placeholder={t('vocabulary.frequency')}
                value={addForm.frequency}
                onChange={(e) => setAddForm({ ...addForm, frequency: e.target.value })}
              />
              <label className="vocab-checkbox">
                <input
                  type="checkbox"
                  checked={addForm.is_important}
                  onChange={(e) => setAddForm({ ...addForm, is_important: e.target.checked })}
                />
                {t('vocabulary.important')}
              </label>
              <button className="vocab-btn vocab-btn-primary" onClick={handleAdd}>{t('save')}</button>
            </div>
          )}

          <div className="vocab-table-wrap">
            <table className="vocab-table">
              <thead>
                <tr>
                  <th className="col-check">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      disabled={words.length === 0}
                      title={t('vocabulary.selectAll')}
                    />
                  </th>
                  <SortableTh column="seq" className="col-seq">{t('vocabulary.seq')}</SortableTh>
                  <SortableTh column="important" className="col-imp">{'★'}</SortableTh>
                  <SortableTh column="frequency" className="col-freq">{t('vocabulary.frequency')}</SortableTh>
                  <SortableTh column="word" className="col-word">{t('vocabulary.word')}</SortableTh>
                  <SortableTh column="meaning" className="col-meaning">{t('vocabulary.meaning')}</SortableTh>
                  <th className="col-note">{t('vocabulary.note')}</th>
                  <th className="col-extra">{t('vocabulary.extra')}</th>
                  <th className="col-actions"></th>
                </tr>
              </thead>
              <tbody>
                {words.length === 0 && (
                  <tr>
                    <td colSpan={9} className="vocab-empty-row">
                      {loading
                        ? t('vocabulary.loading')
                        : (debouncedSearch ? t('vocabulary.noSearchResult', { q: debouncedSearch }) : t('vocabulary.noWords'))}
                    </td>
                  </tr>
                )}
                {words.map((w, idx) => (
                  <React.Fragment key={w.word_id}>
                    {/* 여러 날짜를 함께 볼 때는 날짜 구분 헤더로 묶는다 */}
                    {pagination.grouped_by_date && (idx === 0 || words[idx - 1].study_date !== w.study_date) && (
                      <tr className="vocab-date-group">
                        <td colSpan={9}>
                          <span className="vocab-date-group-label">{w.study_date}</span>
                          <span className="vocab-date-group-count">
                            {t('vocabulary.dateGroupCount', {
                              count: words.filter(x => x.study_date === w.study_date).length,
                            })}
                          </span>
                        </td>
                      </tr>
                    )}
                  {editingId === w.word_id ? (
                    <tr key={w.word_id} className="vocab-editing">
                      <td className="col-check"></td>
                      <td className="col-seq">{w.seq ?? '-'}</td>
                      <td>
                        <input
                          type="checkbox"
                          checked={!!editForm.is_important}
                          onChange={(e) => setEditForm({ ...editForm, is_important: e.target.checked })}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={editForm.frequency}
                          onChange={(e) => setEditForm({ ...editForm, frequency: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          value={editForm.word}
                          onChange={(e) => setEditForm({ ...editForm, word: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          value={editForm.meaning}
                          onChange={(e) => setEditForm({ ...editForm, meaning: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          value={editForm.note}
                          onChange={(e) => setEditForm({ ...editForm, note: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          value={editForm.extra}
                          onChange={(e) => setEditForm({ ...editForm, extra: e.target.value })}
                        />
                      </td>
                      <td className="vocab-actions">
                        <button className="vocab-icon-btn" onClick={() => saveEdit(w.word_id)} title={t('save')}>&#10003;</button>
                        <button className="vocab-icon-btn" onClick={() => setEditingId(null)} title={t('cancel')}>&#10005;</button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={w.word_id} className={selectedIds.includes(w.word_id) ? 'vocab-row-selected' : ''}>
                      <td className="col-check">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(w.word_id)}
                          onChange={() => toggleSelect(w.word_id)}
                        />
                      </td>
                      <td className="col-seq">{w.seq ?? '-'}</td>
                      <td className="col-imp">{w.is_important ? '★' : ''}</td>
                      <td className="col-freq">{w.frequency ?? '-'}</td>
                      <td className="col-word">{w.word}</td>
                      <td className="col-meaning">{w.meaning}</td>
                      <td className="col-note">{w.note || ''}</td>
                      <td className="col-extra">{w.extra || ''}</td>
                      <td className="vocab-actions">
                        <button className="vocab-icon-btn" onClick={() => startEdit(w)} title={t('edit')}>&#9998;</button>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.total > 0 && (
            <div className="vocab-pagination">
              <label className="vocab-pagesize">
                {t('vocabulary.perPage')}
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); reloadWords(1); }}
                >
                  {[20, 50, 100, 200].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>

              <span className="vocab-page-range">
                {t('vocabulary.pageRange', {
                  from: (pagination.page - 1) * pagination.limit + 1,
                  to: Math.min(pagination.page * pagination.limit, pagination.total),
                  total: pagination.total,
                })}
              </span>

              <div className="vocab-page-nav">
                <button
                  className="vocab-btn"
                  disabled={pagination.page <= 1}
                  onClick={() => reloadWords(1)}
                  title={t('vocabulary.firstPage')}
                >
                  &#8676;
                </button>
                <button
                  className="vocab-btn"
                  disabled={pagination.page <= 1}
                  onClick={() => reloadWords(pagination.page - 1)}
                >
                  &#8592;
                </button>
                <span className="vocab-page-current">
                  {pagination.page} / {pagination.total_pages}
                </span>
                <button
                  className="vocab-btn"
                  disabled={pagination.page >= pagination.total_pages}
                  onClick={() => reloadWords(pagination.page + 1)}
                >
                  &#8594;
                </button>
                <button
                  className="vocab-btn"
                  disabled={pagination.page >= pagination.total_pages}
                  onClick={() => reloadWords(pagination.total_pages)}
                  title={t('vocabulary.lastPage')}
                >
                  &#8677;
                </button>
              </div>
            </div>
          )}
        </section>
      </div>

      {/* 업로드 다이얼로그 */}
      {uploadOpen && (
        <div className="vocab-overlay" onClick={() => setUploadOpen(false)}>
          <div className="vocab-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>{t('vocabulary.uploadCsv')}</h3>

            <label className="vocab-field">
              <span>{t('vocabulary.studyDate')}</span>
              <input type="date" value={uploadDate} onChange={(e) => setUploadDate(e.target.value)} />
            </label>

            <label className="vocab-field">
              <span>{t('vocabulary.aliasLabel')}</span>
              <input
                type="text"
                maxLength={100}
                value={uploadAlias}
                onChange={(e) => setUploadAlias(e.target.value)}
                placeholder={t('vocabulary.aliasPlaceholder')}
              />
            </label>

            <div
              className={`vocab-dropzone${dragOver ? ' drag-over' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                handleFileSelect(e.dataTransfer.files?.[0]);
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={(e) => handleFileSelect(e.target.files?.[0])}
              />
              {uploadFile ? (
                <strong>{uploadFile.name}</strong>
              ) : (
                <span>{t('vocabulary.dropHint')}</span>
              )}
            </div>

            <div className="vocab-format-hint">
              <strong>{t('vocabulary.formatTitle')}</strong>
              <code>{t('vocabulary.formatColumns')}</code>
              <small>{t('vocabulary.formatNote')}</small>
            </div>

            {uploadResult && (
              <div className="vocab-upload-result">
                <p>
                  {t('vocabulary.resultInserted', { count: uploadResult.inserted_rows })} ·{' '}
                  {t('vocabulary.resultUpdated', { count: uploadResult.updated_rows })} ·{' '}
                  {t('vocabulary.resultSkipped', { count: uploadResult.skipped_rows })}
                </p>
                {uploadResult.header_detected && (
                  <p className="vocab-header-note">{t('vocabulary.headerDetected')}</p>
                )}
                {uploadResult.warning_count > 0 && (
                  <details>
                    <summary>{t('vocabulary.resultWarnings', { count: uploadResult.warning_count })}</summary>
                    <ul>
                      {uploadResult.warnings.map((w, i) => (
                        <li key={i}>
                          {w.line > 0 && <>#{w.line} </>}
                          {t(`vocabulary.warn.${w.reason}`, { defaultValue: w.reason })}
                          {w.value && <> — {w.value}</>}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}

            <div className="vocab-dialog-actions">
              <button className="vocab-btn" onClick={() => setUploadOpen(false)}>{t('close')}</button>
              <button className="vocab-btn vocab-btn-primary" onClick={handleUpload} disabled={loading || !uploadFile}>
                {loading ? t('vocabulary.uploading') : t('vocabulary.upload')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vocabulary;
